/**
 * React Hook: Apex Lint (chat editor only)
 *
 * Provides a LintApi-compatible interface for the @ncoder/code-editor.
 * Calls the ide-service POST /validate/apex endpoint.
 * Debounces requests by 500ms to avoid excessive calls during typing.
 */

import { useCallback, useRef, useMemo } from 'react';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { ideServiceApi, ideAuthHeaders } from '@/services/ide';
import type { LintApi, LintResult } from '@ncoder/code-editor';

interface ValidateApexResponse {
  diagnostics: Array<{
    line: number;
    column: number;
    endLine?: number;
    endColumn?: number;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'hint';
  }>;
}

const LINTABLE_EXTENSIONS = ['.cls', '.trigger'];

function isLintable(fileName: string): boolean {
  return LINTABLE_EXTENSIONS.some((ext) => fileName.endsWith(ext));
}

export function useApexLint(): LintApi {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const lint = useCallback(
    async (code: string, fileName: string): Promise<LintResult[]> => {
      if (!isLintable(fileName)) return [];

      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();

      return new Promise<LintResult[]>((resolve) => {
        debounceRef.current = setTimeout(async () => {
          const controller = new AbortController();
          abortRef.current = controller;
          try {
            const { data } = await ideServiceApi.post<ValidateApexResponse>(
              '/validate/apex',
              { code, fileName },
              { signal: controller.signal, headers: ideAuthHeaders(orgId) },
            );
            resolve(
              (data.diagnostics || []).map((d) => ({
                line: d.line,
                column: d.column,
                endLine: d.endLine,
                endColumn: d.endColumn,
                message: d.message,
                severity: d.severity,
              })),
            );
          } catch (err: unknown) {
            if (err instanceof Error && err.name === 'CanceledError') {
              resolve([]);
              return;
            }
            console.warn('[useApexLint] Validation failed:', err);
            resolve([]);
          }
        }, 500);
      });
    },
    [orgId],
  );

  return useMemo(() => ({ lint }), [lint]);
}
